import { Injectable, inject } from '@angular/core';
import {
  ColDef,
  IDateFilterParams,
  INumberFilterParams,
  ITextFilterParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { CellRenderService } from './cell-render.service';
import { GridUtilsService } from './grid-utils.service';
import { GridValueGetterService } from './grid-value-getter.service';
import { GridValueFormatterService } from './grid-value-formatter.service';

@Injectable({
  providedIn: 'root',
})
/**
 * Service for configuring and managing grid column definitions in AgGrid.
 * Provides utilities for generating default column definitions,
 * merging user-specified configurations, and setting up renderers, formatters, and filters.
 */
export class GridColumnService {
  private gridUtils = inject(GridUtilsService);
  private gridValueGetterService = inject(GridValueGetterService);
  private gridValueFormatterService = inject(GridValueFormatterService);
  private cellRenderService = inject(CellRenderService);

  constructor() {}

  /**
   * Combines user-specified and automatically inferred column definitions for the grid.
   * Infers missing columns based on sample data and merges with user-provided definitions.
   * @param gridColumnConfig - User-defined column configurations from report settings.
   * @param firstRowData - Sample row data to infer default column definitions for missing fields.
   * @param addFilters - Boolean to determine if floating filters should be enabled.
   * @returns Array of ColDef objects for use in Ag-Grid.
   */
  configureColumns(
    gridColumnConfig: Record<string, any>,
    rowData: any[],
    addFilters: boolean,
  ): ColDef[] {
    // Ensure gridColumnConfig is an array
    const providedGridColumns = Array.isArray(gridColumnConfig)
      ? gridColumnConfig
      : [gridColumnConfig];
    const derivedGridColumns = this.deriveUnspecifiedGridColumns(
      providedGridColumns,
      rowData[0], // just need 1 row to determine column names
    );
    const allGridColumns = providedGridColumns.concat(derivedGridColumns);
    return this.getColumnDefs(allGridColumns, rowData, addFilters);
  }

  /**
   * Identifies fields in the sample data that are not explicitly defined in the provided column configurations.
   * Generates default column definitions for these unspecified fields.
   */
  deriveUnspecifiedGridColumns(
    providedColumns: Record<string, any>[],
    firstRowData: any[],
  ): Record<string, any>[] {
    const providedFields = providedColumns.map((col) => col.field);
    const firstRowDataKeys = Object.keys(firstRowData);
    return firstRowDataKeys
      .filter((key) => !providedFields.includes(key))
      .map((key) => {
        return { field: key };
      });
  }

  /**
   * Generates the full set of column definitions by applying renderers, formatters, filters, and styles.
   * Combines user-defined and default column configurations and determines settings based on data types.
   */
  getColumnDefs(
    gridColumns: Record<string, any>[],
    rowData: any[],
    addFilters: boolean,
  ): ColDef[] {
    return (
      gridColumns
        // Filter out columns explicitly marked for removal in reportConfig.gridColumns
        // by checking the `removeFromGrid` property.
        .filter((columnConfig) => !columnConfig.removeFromGrid)
        // Determine column data type
        .map((columnConfig) => {
          const columnDataType = this.getColumnType(
            columnConfig.field,
            rowData,
          );
          // Apply the corresponding column type configuration and add grid-specific settings
          return {
            ...columnConfig, // Include config-specified properties
            headerName:
              columnConfig.headerName ??
              this.gridUtils.toSentenceCase(columnConfig.field),

            // Enable/disable floating filters to ALL colDefs if addFilters option is specified in gridCustom
            // (backwards compatible with Report Builder that can add floating filters to columns individually)
            ...(addFilters && {
              floatingFilter: addFilters,
            }),

            // Apply configurations for the determined column type
            ...this.colDefTypeMap[columnDataType],

            // TODO: https://agrigate.atlassian.net/browse/DATA-2546
            // TODO: https://agrigate.atlassian.net/browse/DATA-2547
            filter: 'agTextColumnFilter',

            // Use custom cell renderer if specified
            ...(columnConfig.cellRenderer && {
              cellRenderer: (params) =>
                this.cellRenderService.getCellRenderer(params, columnConfig),
            }),

            // Use custom value getter if specified
            // This function takes precedence over default functions applied in the methods called by this.colDefTypeMap
            ...(columnConfig.valueGetter && {
              valueGetter: (params) =>
                this.gridValueGetterService.getValueGetter(
                  params,
                  columnConfig,
                ),
            }),

            // Use custom value formatting if specified
            // This formatter takes precedence over default formatters applied in the methods called by this.colDefTypeMap
            ...(columnConfig.valueFormatter && {
              valueFormatter: (params) =>
                this.gridValueFormatterService.getValueFormatter(
                  params,
                  columnConfig,
                ),
            }),
          };
        })
    );
  }

  /**
   * Determines the data type of a column based on the field's sample data.
   * @returns String indicating the column data type: 'text', 'integer', 'float', or 'date'.
   */
  getColumnType(
    fieldName: string,
    rowData: any[],
  ): 'text' | 'integer' | 'float' | 'date' {
    // extract all non-empty values for this column
    const columnValues = rowData.map((row) => row[fieldName]);
    const nonEmptyValues = columnValues.filter((value) => value != null);

    if (nonEmptyValues.length > 0) {
      // first check if all values are dates
      const allDates = nonEmptyValues.every(
        (value) => this.gridUtils.extractDateFromParams(value) instanceof Date,
      );
      if (allDates) {
        return 'date';
      }

      // then check if all values are numbers
      const allNumbers = nonEmptyValues.every((value) =>
        this.gridUtils.isNumber(value),
      );
      if (allNumbers) {
        // Check if any number is decimal
        const hasDecimal = nonEmptyValues.some((value) =>
          this.gridUtils.isDecimal(value),
        );
        return hasDecimal ? 'float' : 'integer';
      }
    }

    // fall back to text if any value is not a number or all values are empty
    return 'text';
  }

  colDefTypeMap: Record<string, any> = {
    text: this.getTextColumnDef(),
    integer: this.getIntColumnDef(),
    float: this.getFloatColumnDef(),
    date: this.getDateColumnDef(),
  };

  getTextColumnDef() {
    return {
      valueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getTextValue(params),
      filterValueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getTextValue(params),

      valueFormatter: (params: ValueFormatterParams) => `${params.value || ''}`,

      filterParams: {
        buttons: ['apply', 'clear', 'reset'],
        closeOnApply: true,
        caseSensitive: false,
        defaultOption: 'contains',
        filterOptions: ['contains', 'notContains', 'equals'],
        maxNumConditions: 1,
      } as ITextFilterParams,
      cellClass: 'ag-left-aligned-cell',
    };
  }

  getIntColumnDef() {
    return {
      valueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getIntValue(params),
      filterValueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getTextValue(params),

      valueFormatter: (params: ValueFormatterParams) =>
        this.gridValueFormatterService.floatFormatter(params, 0),

      filterParams: {
        buttons: ['apply', 'clear', 'reset'],
        closeOnApply: true,
        defaultOption: 'contains',
        filterOptions: ['contains', 'notContains', 'equals'],
        maxNumConditions: 1,
      } as INumberFilterParams,

      cellClass: 'ag-right-aligned-cell',
    };
  }

  getFloatColumnDef() {
    return {
      valueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getTextValue(params),
      filterValueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getTextValue(params),

      valueFormatter: (params: ValueFormatterParams) =>
        this.gridValueFormatterService.floatFormatter(params),

      filterParams: {
        buttons: ['apply', 'clear', 'reset'],
        closeOnApply: true,
        defaultOption: 'contains',
        filterOptions: ['contains', 'notContains', 'equals'],
        maxNumConditions: 1,
      } as INumberFilterParams,

      cellClass: 'ag-right-aligned-cell',
    };
  }

  getDateColumnDef() {
    return {
      valueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getDateTimeValue(params),
      filterValueGetter: (params: ValueGetterParams) =>
        this.gridValueGetterService.getDateTimeValue(params),

      valueFormatter: (params: ValueFormatterParams) =>
        this.gridValueFormatterService.dateTimeFormatter(params),

      filterParams: {
        buttons: ['apply', 'clear', 'reset'],
        closeOnApply: true,
        inRangeInclusive: true,
        browserDatePicker: true,
        defaultOption: 'contains',
        filterOptions: ['contains'],
        maxNumConditions: 1,
      } as IDateFilterParams,

      cellClass: 'ag-left-aligned-cell',
    };
  }
}
